import { type ReactElement, type ReactNode } from 'react'
import { type CompanyData, type TeamData } from '@terros/common'
import { useTeamHierarchyFocusViewFeature } from '@hooks'
import { NotReady } from '../NotReady'
import { type ConnectionLayerOptions } from '../connectionLayer'
import { TeamDragProvider, TeamHierarchyDisplay, TeamHierarchyProvider } from './hierarchyFocus'
import {
  TeamDragProvider as LegacyTeamDragProvider,
  TeamHierarchyDisplay as LegacyTeamHierarchyDisplay,
  TeamHierarchyProvider as LegacyTeamHierarchyProvider,
} from './hierarchy'

type TeamHierarchyProps = {
  teams: TeamData[]
  company: CompanyData
  connectionSettings: ConnectionLayerOptions
  withPermissions?: boolean
  canEdit?: boolean
  hideLevelLabels?: boolean
  footer?: ReactNode
}

export const TeamHierarchy = (props: TeamHierarchyProps): ReactNode => {
  const { isLoading, isEnabled } = useTeamHierarchyFocusViewFeature()

  if (isLoading) return <NotReady isLoading inline id='Team Hierarchy' />
  if (isEnabled) return <FocusHierarchy {...props} />

  return <LegacyHierarchy {...props} />
}

const LegacyHierarchy = ({
  teams,
  company,
  connectionSettings,
  withPermissions,
  canEdit,
  hideLevelLabels,
  footer,
}: TeamHierarchyProps): ReactElement => (
  <LegacyTeamHierarchyProvider
    canEdit={canEdit}
    company={company}
    connectionSettings={connectionSettings}
    hideLevelLabels={hideLevelLabels}
    teams={teams}
    withPermissions={withPermissions}
  >
    <LegacyTeamDragProvider>
      <LegacyTeamHierarchyDisplay footer={footer} />
    </LegacyTeamDragProvider>
  </LegacyTeamHierarchyProvider>
)

const FocusHierarchy = ({
  teams,
  company,
  connectionSettings,
  withPermissions,
  canEdit,
  footer,
}: TeamHierarchyProps): ReactElement => (
  <TeamHierarchyProvider
    canEdit={canEdit}
    company={company}
    connectionSettings={connectionSettings}
    teams={teams}
    withPermissions={withPermissions}
  >
    <TeamDragProvider>
      <TeamHierarchyDisplay footer={footer} />
    </TeamDragProvider>
  </TeamHierarchyProvider>
)
